import { useState } from 'react'
import { api } from '@/api/client'
import type { ScienceException } from '@/lib/types'

export type BuilderState = { job_id?: string; status?: string; blocker?: string }

const ACTIVE = ['queued', 'running', 'capacity_wait', 'pending_delivery']
const RESUMABLE = ['blocked_dev', 'failed']

/** A pipeline step that stopped (import, quality-check preparation, builder restart), with its retry. */
export function PipelineException({ exception, onRetried }: { exception?: ScienceException | null; onRetried: () => void }) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!exception) return null
  const retry = () => {
    setBusy(true); setError('')
    void api.post(`/eng/scientific/work/${exception.job_id}/retry`, {}).then(onRetried)
      .catch(e => setError((e as Error).message)).finally(() => setBusy(false))
  }
  return <div className="note err" role="alert">
    <p><strong>The pipeline stopped and needs a developer.</strong> {exception.error}</p>
    {error && <p>{error}</p>}
    <button disabled={busy} onClick={retry}>Retry this step</button>
  </div>
}

/** The authoring-service builder of a workflow, for developers: it runs on its own, so this only
 * unblocks it — cancel it, resume a stopped job, or restart it with instructions. */
export function WorkflowBuilder({ workflowId, revision, builder, compiled, onChanged }: {
  workflowId: string; revision: number; builder?: BuilderState | null; compiled: boolean; onChanged: () => void
}) {
  const [message, setMessage] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const status = builder?.job_id ? builder.status ?? 'unavailable' : undefined
  const active = !!status && ACTIVE.includes(status)
  const resumable = !!status && RESUMABLE.includes(status)
  async function send(request: () => Promise<unknown>) {
    setBusy(true); setError('')
    try { await request(); setMessage(''); onChanged() } catch (e) { setError((e as Error).message) } finally { setBusy(false) }
  }
  const start = () => send(() => api.post(`/scientific/workflows/${workflowId}/builder`, { revision, ...(message.trim() ? { message: message.trim() } : {}) }))
  const resume = () => send(() => api.post(`/eng/tasks/${workflowId}/authoring/${builder?.job_id}/resume`, { message: message.trim() }))
  const cancel = () => send(() => api.post(`/eng/tasks/${workflowId}/authoring/${builder?.job_id}/cancel`, {}))
  return <details className="science-builder">
    <summary>Builder controls{status && ` · ${status.replace(/_/g, ' ')}`}</summary>
    {builder?.blocker && <p className="small">{builder.blocker}</p>}
    {error && <p className="note err" role="alert">{error}</p>}
    {active
      ? <button disabled={busy} onClick={() => void cancel()}>Cancel builder</button>
      : <form onSubmit={e => { e.preventDefault(); void (resumable ? resume() : start()) }}>
        <label className="field"><span>{!status ? 'Notes for the builder (optional)' : resumable ? 'Instructions to resume' : compiled ? 'What the builder must change (review feedback is included)' : 'What the builder must change'}</span>
          <textarea value={message} onChange={e => setMessage(e.target.value)} maxLength={4000} required={!!status} /></label>
        <button disabled={busy || (!!status && !message.trim())}>{!status ? 'Start builder' : resumable ? 'Resume builder' : 'Restart builder'}</button>
      </form>}
  </details>
}
